import classnames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  CheckboxInput,
  TextInput,
  IcNoDataEvent as noDataEventIcon,
} from '@/shared/ui';
import approveIcon from './assets/approve-icon.svg';
import cancelIcon from './assets/cancel-icon.svg';
import useHrChartOptions from './useHrChartOptions';
import './style.scss';

export type HrChartOptionsProps = {
  // never read inside this component, but forwarded from hrSummary/index.tsx and
  // asserted by hrSummary/index.test.tsx - keep declared.

  profileId?: any;
  timezoneOffset?: number;
  isOldUI?: boolean;
  isProcessArtifactEvent?: boolean;
};

/**
 * Renders the editable min, avg and max heart-rate summary boxes plus the artifact and no-data legend under the HR chart. Shows a skeleton while useHrChartOptions reports loading and nothing at all once it reports empty data, then maps each HR item to a numeric input with inline cancel and approve buttons that appear only after the value is edited.
 *
 * @param props - Study context plus the isProcessArtifactEvent and isOldUI display switches.
 * @returns The HR summary inputs and chart legend, a loading skeleton, or null when there is no data.
 */
const HrChartOptions = ({
  timezoneOffset,
  isOldUI,
  isProcessArtifactEvent,
}: HrChartOptionsProps) => {
  const { isLoadingHrChart, isEmptyData, renderHrItems, onClickCancel, onChangeValue } =
    useHrChartOptions({ timezoneOffset });

  if (isLoadingHrChart) {
    return (
      <div className="navigator-skeleton-container">
        <div className="skeleton-container">
          <Skeleton height="100%" />
        </div>
      </div>
    );
  }

  if (isEmptyData) return null;

  return (
    <>
      <div id="hrs-info" className="hrs-info">
        {!isProcessArtifactEvent && (
          <div className="hrs-info-container">
            {renderHrItems.map((item, index) => (
              <div className="hrs-item" key={item.title}>
                <div className="hrs-header">
                  <div className="hrs-title">{item.title}</div>
                  {!item.disabled && (
                    <>
                      <div className="hrs-time">{item?.time}</div>
                      {item.viewBtn && (
                        <button
                          id={`hrs-view-btn-${index}`}
                          className="hrs-view-btn"
                          onClick={item.viewBtn.onClick}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              item.viewBtn.onClick();
                            }
                          }}
                        >
                          {item.viewBtn.text}
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="hrs-content">
                  <TextInput
                    name={undefined as any} // pre-existing bug: TextInput's `name` is required but never passed here
                    isNumberFormat
                    customSuffix={true as any} // pre-existing bug: customSuffix is typed string, this is boolean
                    suffix="bpm"
                    isError={item.isError}
                    errorMessage="Invalid value"
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={(name, value) => {
                      onChangeValue(value, item.type);
                    }}
                    id={`edit-hr-input-${item.type}${item.placeholder === 'NA' ? '-NA' : ''}`}
                    decimalSeparator={false}
                    disabled={item.disabled}
                  />
                  {item.isEdited && (
                    <div className="hrs-btn">
                      <IconButton
                        id={`hr-cancel-edit-btn-${index}`}
                        className="btn btn-cancel"
                        isOutline
                        iconComponent={<img src={cancelIcon} alt="Cancel icon" />}
                        onClick={() => {
                          onClickCancel(item.type);
                        }}
                      />
                      <IconButton
                        id={`hr-approve-edit-btn-${index}`}
                        isFilled
                        className="btn btn-approved"
                        isOutline
                        iconComponent={<img src={approveIcon} alt="Approve icon" />}
                        onClick={item.saveBtn.onClick}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div id="hr-chart-options" className={isOldUI ? 'ecg-options-OLD' : 'ecg-options'}>
        <div className="event-info-container">
          <CheckboxInput
            className={classnames('event-info-checkbox', '--artifact', 'always-visible')}
            id="artifact"
            name="artifact"
            data={
              {
                label: 'Artifact',
                value: false, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
              } as any
            }
            checked={false}
            onChange={() => {}}
          />
          <div className={classnames('event-info-checkbox', '--no-data')}>
            <img src={noDataEventIcon} alt="no-data-event" />
            <div>No data</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HrChartOptions;
